import {
	Card,
	Space,
	TabsProps,
	Typography,
	Dropdown,
	Menu,
} from '@arco-design/web-react';
import {
	IconClose,
	IconPlus,
	IconDown,
	IconDragDotVertical,
} from '@arco-design/web-react/icon';
import { cloneDeep } from 'lodash';
import React from 'react';
import { t } from '@bay-email/core';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BayTooltip from '@bay-ui/BayTooltip';

interface PlatformOption<T> {
	key: string;
	label: string;
	icon?: string;
	data: T;
	disabled?: boolean;
	tooltip?: React.ReactNode;
	tooltipPlacement?: string;
}

export interface EditGridTabProps<T extends any = any> extends Omit<
	TabsProps,
	'onChange'
> {
	value: Array<T>;
	renderItem: (item: T, index: number) => React.ReactNode;
	onChange: (vals: Array<T>) => any;
	additionItem?: T;
	platformOptions?: PlatformOption<T>[];
	label: string;
}

interface SortableItemProps<T> {
	id: string;
	index: number;
	item: T;
	renderItem: (item: T, index: number) => React.ReactNode;
	renderAddButton: (index?: number) => React.ReactNode;
	onDelete: (index: number) => void;
}

function SortableItem<T>({
	id,
	index,
	item,
	renderItem,
	renderAddButton,
	onDelete,
}: SortableItemProps<T>) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
		useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<Card.Grid
			ref={setNodeRef}
			style={{ width: '100%', ...style, border: 'none' }}
			data-dnd-kit-sortable-item
		>
			<Card
				bordered={false}
				title={
					<Space>
						<IconDragDotVertical
							{...attributes}
							{...listeners}
							style={{ cursor: 'grab', color: '#666' }}
						/>
						<Typography.Text>
							{t('Item')} {index + 1}
						</Typography.Text>
					</Space>
				}
				extra={
					<Space size='large'>
						{renderAddButton(index)}
						<IconClose
							style={{ color: '#000', cursor: 'pointer' }}
							onClick={() => onDelete(index)}
						/>
					</Space>
				}
			>
				{renderItem(item, index)}
			</Card>
		</Card.Grid>
	);
}
export function EditGridTab<T extends any = any>(props: EditGridTabProps<T>) {
	const { value, additionItem, platformOptions } = props;

	const onAdd = (index?: number, platformData?: T) => {
		let newItem: T;
		if (platformData) {
			newItem = cloneDeep(platformData);
		} else if (additionItem) {
			newItem = cloneDeep(additionItem);
		} else if (Array.isArray(value) && value.length > 0) {
			newItem = cloneDeep(value[index || 0]);
		} else {
			// Fallback - this shouldn't happen if additionItem is provided
			newItem = {} as T;
		}

		const safeValue = Array.isArray(value) ? value : [];
		if (typeof index === 'number') {
			safeValue.splice(index + 1, 0, newItem);
		} else {
			safeValue.push(newItem);
		}
		props.onChange([...safeValue]);
	};

	const renderAddButton = (index?: number) => {
		if (!platformOptions) {
			return (
				<IconPlus
					style={{ color: '#000', cursor: 'pointer' }}
					onClick={() => onAdd(index)}
				/>
			);
		}

		const droplist = (
			<Menu>
				{platformOptions.map(option => (
					<Menu.Item
						key={option.key}
						onClick={() => onAdd(index, option.data)}
						disabled={option?.disabled}
					>
						<BayTooltip
							disabled={!option?.disabled}
							overlay={option?.tooltip}
							placement={option?.tooltipPlacement}
						>
							<div
								className={option?.disabled ? 'disabled' : ''}
								style={{ display: 'flex', alignItems: 'center', width: '100%' }}
							>
								<Space style={{ width: '100%' }}>
									{option.icon && (
										<img
											src={option.icon}
											alt={option.label}
											style={{ width: 16, height: 16 }}
										/>
									)}

									<span>{option.label}</span>
								</Space>
							</div>
						</BayTooltip>
					</Menu.Item>
				))}
				{additionItem && (
					<Menu.Item
						key='custom'
						onClick={() => onAdd(index)}
					>
						<Space>
							<IconPlus style={{ fontSize: 12 }} />
							{t('Custom')}
						</Space>
					</Menu.Item>
				)}
			</Menu>
		);

		return (
			<Dropdown
				droplist={droplist}
				position='bottom'
			>
				<Space style={{ cursor: 'pointer', color: '#000' }}>
					<IconPlus />
					<IconDown />
				</Space>
			</Dropdown>
		);
	};

	const onDelete = (index: number) => {
		props.onChange(
			(Array.isArray(value) ? value : []).filter((_, vIndex) => Number(index) !== vIndex),
		);
	};

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (active.id !== over?.id) {
			const safeValue = Array.isArray(value) ? value : [];
			const oldIndex = safeValue.findIndex(
				(item, index) => `item-${index}` === active.id,
			);
			const newIndex = safeValue.findIndex((item, index) => `item-${index}` === over?.id);

			const newValue = arrayMove(safeValue, oldIndex, newIndex);
			props.onChange(newValue);
		}
	};

	return (
		<div style={{ padding: 0 }}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragEnd={handleDragEnd}
			>
				<SortableContext
					items={(Array.isArray(value) ? value : []).map((_, index) => `item-${index}`)}
					strategy={verticalListSortingStrategy}
				>
					{(Array.isArray(value) ? value : []).map((item, index) => (
						<SortableItem
							key={`item-${index}`}
							id={`item-${index}`}
							index={index}
							item={item}
							renderItem={props.renderItem}
							renderAddButton={renderAddButton}
							onDelete={onDelete}
						/>
					))}
				</SortableContext>
			</DndContext>

			{(!Array.isArray(value) || value.length === 0) && (
				<Card.Grid style={{ width: '100%' }}>
					<Card
						title={t('No items')}
						extra={renderAddButton()}
					>
						<Typography.Text type='secondary'>
							{platformOptions
								? t('Select a platform from the dropdown or choose custom')
								: t('Click the + icon to add your first item')}
						</Typography.Text>
					</Card>
				</Card.Grid>
			)}
		</div>
	);
}
